import { useState, useEffect } from "react";
import { toast } from "sonner";
import { 
  Loader2,
  User,
  Mail,
  Phone,
  Building,
  Plane,
  MessageSquare
} from "lucide-react";
import { getLeads, updateLeadStatut } from "@/lib/api";

const typeIcons = {
  proprietaire: Building,
  investisseur: User,
  voyageur: Plane
};

const typeLabels = {
  proprietaire: "Propriétaire",
  investisseur: "Investisseur",
  voyageur: "Voyageur"
};

const statusColors = {
  nouveau: "bg-blue-100 text-blue-700",
  contacte: "bg-yellow-100 text-yellow-700",
  en_cours: "bg-purple-100 text-purple-700",
  converti: "bg-green-100 text-green-700",
  perdu: "bg-red-100 text-red-700"
};

const statusLabels = {
  nouveau: "Nouveau",
  contacte: "Contacté",
  en_cours: "En cours",
  converti: "Converti",
  perdu: "Perdu"
};

export default function LeadsAdminPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await getLeads();
      setLeads(res.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateLeadStatut(id, newStatus);
      toast.success("Statut mis à jour !");
      loadData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const filteredLeads = filter 
    ? leads.filter(l => l.type_lead === filter)
    : leads;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#004333]" />
      </div>
    );
  }

  return (
    <div data-testid="leads-admin-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-display text-[#004333]">Leads entrants</h2>
          <p className="text-gray-500">{leads.length} lead(s)</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter("")}
            className={`px-4 py-2 text-sm ${!filter ? "bg-[#004333] text-white" : "bg-gray-100 text-gray-600"}`}
          >
            Tous
          </button>
          <button
            onClick={() => setFilter("proprietaire")}
            className={`px-4 py-2 text-sm ${filter === "proprietaire" ? "bg-[#004333] text-white" : "bg-gray-100 text-gray-600"}`}
          >
            Propriétaires
          </button>
          <button
            onClick={() => setFilter("investisseur")}
            className={`px-4 py-2 text-sm ${filter === "investisseur" ? "bg-[#004333] text-white" : "bg-gray-100 text-gray-600"}`}
          >
            Investisseurs
          </button>
          <button
            onClick={() => setFilter("voyageur")}
            className={`px-4 py-2 text-sm ${filter === "voyageur" ? "bg-[#004333] text-white" : "bg-gray-100 text-gray-600"}`}
          >
            Voyageurs
          </button>
        </div>
      </div>

      {filteredLeads.length > 0 ? (
        <div className="space-y-4">
          {filteredLeads.map((lead) => {
            const TypeIcon = typeIcons[lead.type_lead] || User;
            return (
              <div key={lead.id} className="bg-white border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#004333]/10 flex items-center justify-center">
                      <TypeIcon className="w-6 h-6 text-[#004333]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#004333]">{lead.nom}</h3>
                      <span className="text-sm text-gray-500">{typeLabels[lead.type_lead]}</span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 text-xs font-medium ${statusColors[lead.statut]}`}>
                    {statusLabels[lead.statut]}
                  </span>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4 text-[#C9922A]" />
                    {lead.email}
                  </div>
                  {lead.telephone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4 text-[#C9922A]" />
                      {lead.telephone}
                    </div>
                  )}
                  {lead.ville && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Ville:</span> {lead.ville}
                    </div>
                  )}
                  {lead.type_bien && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Type:</span> {lead.type_bien}
                    </div>
                  )}
                  {lead.surface && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Surface:</span> {lead.surface} m²
                    </div>
                  )}
                </div>
                
                {lead.message && (
                  <div className="bg-gray-50 p-4 mb-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <MessageSquare className="w-4 h-4" />
                      Message
                    </div>
                    <p className="text-sm text-gray-600">{lead.message}</p>
                  </div>
                )}
                
                <div className="flex gap-2 pt-4 border-t border-gray-100">
                  <select
                    value={lead.statut}
                    onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                    className="input-hh text-sm py-2"
                    data-testid={`lead-status-${lead.id}`}
                  >
                    <option value="nouveau">Nouveau</option>
                    <option value="contacte">Contacté</option>
                    <option value="en_cours">En cours</option>
                    <option value="converti">Converti</option>
                    <option value="perdu">Perdu</option>
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 p-16 text-center">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-display text-xl text-[#004333] mb-2">Aucun lead</h3>
          <p className="text-gray-500">Les demandes de contact apparaîtront ici.</p>
        </div>
      )}
    </div>
  );
}
